/**
 * ResizeHandle.tsx — ui/standardize-resize-handle
 *
 * Standard bottom-right resize-corner indicator for every custom-resizable
 * modal/panel in Relay. None of them use the browser's native `resize: both`
 * (which would draw its own grip automatically) — each one drives resize via
 * its own mousedown/mousemove handler, so each one needs an explicit visual
 * affordance or there's nothing telling a human it's draggable.
 *
 * Root cause investigated before building this: the three prior per-component
 * attempts (ERModal's SVG lines at 0.45 opacity, ProjectBackgroundModal's and
 * ControlCenter's single-line gradient stripe at ~33% alpha) all rendered
 * correctly — confirmed by mounting the real ERModal component and
 * screenshotting it, checking computed styles and hit-testing. Nothing was
 * hidden or broken; the glyphs were just too small and faint to register as
 * UI at a glance. This uses solid brand teal (#5FC4C0 — already the intended
 * color in both prior attempts, just previously at ~33% alpha) at near-full
 * opacity with a bolder stroke instead.
 */
import React from "react";

export interface ResizeHandleProps {
  onMouseDown: (e: React.MouseEvent) => void;
  /** Extra inline styles merged over the defaults (e.g. to nudge position for a host with different padding). */
  style?: React.CSSProperties;
}

export function ResizeHandle({ onMouseDown, style }: ResizeHandleProps) {
  const [hover, setHover] = React.useState(false);
  return (
    <div
      role="button"
      aria-label="Resize"
      title="Drag to resize"
      onMouseDown={onMouseDown}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "absolute",
        right: 3,
        bottom: 3,
        width: 18,
        height: 18,
        cursor: "nwse-resize",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "flex-end",
        opacity: hover ? 1 : 0.85,
        transform: hover ? "scale(1.08)" : "scale(1)",
        transition: "opacity 120ms, transform 120ms",
        zIndex: 5,
        ...style,
      }}
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path
          d="M1.5 14.5 L14.5 1.5 M6.5 14.5 L14.5 6.5 M11.5 14.5 L14.5 11.5"
          stroke="#5FC4C0"
          strokeWidth="1.75"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
}

export default ResizeHandle;
